import React, { Children, useCallback, useEffect } from 'react';

import ReactScrollWheelHandler from 'react-scroll-wheel-handler';

import './ArtifactsSlider.scss';
import { ArtifactsSliderSlide } from './Slide';

interface YatArtifactsSliderProps {
    className?: string;
    expanded?: boolean;
    children: React.ReactNode;
    activeIndex: number;
    onChange?: (currentIndex: number) => void;
}

export const ArtifactsSlider = ({
    className = '',
    expanded = false,
    children,
    activeIndex,
    onChange,
}: YatArtifactsSliderProps) => {
    const goToSlide = useCallback(
        (newIndex) => {
            if (expanded) return;
            if (!Children.toArray(children)[newIndex]) return;
            if (!onChange) return;

            onChange(newIndex);
        },
        [onChange, children, expanded],
    );

    const goToNextSlide = useCallback(() => {
        goToSlide(activeIndex + 1);
    }, [activeIndex, goToSlide]);

    const goToPreviousSlide = useCallback(() => {
        goToSlide(activeIndex - 1);
    }, [activeIndex, goToSlide]);

    const handleScrollDown = useCallback(
        function handleScrollDown() {
            goToNextSlide();
        },
        [goToNextSlide],
    );

    const handleScrollUp = useCallback(
        function handleScrollDown() {
            goToPreviousSlide();
        },
        [goToPreviousSlide],
    );

    const handleKeyDown = useCallback(
        (event) => {
            if (event.key === 'ArrowDown') {
                goToNextSlide();
            } else if (event.key === 'ArrowUp') {
                goToPreviousSlide();
            }
        },
        [goToNextSlide, goToPreviousSlide],
    );

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    return (
        <div className={`yat-artifacts__slider ${className}`}>
            <ReactScrollWheelHandler upHandler={handleScrollUp} downHandler={handleScrollDown}>
                {Children.map(children, (child, i) => {
                    return (
                        <ArtifactsSliderSlide
                            key={Children[i]?.key}
                            activeIndex={activeIndex}
                            index={i}
                            expanded={expanded}
                        >
                            {child}
                        </ArtifactsSliderSlide>
                    );
                })}
            </ReactScrollWheelHandler>
        </div>
    );
};
